import { useCheckAccountType } from '@cmg/auth';
import { UUID } from '@cmg/common';
import { FiberManualRecordIcon, Tooltip } from '@cmg/design-system';
import { useDebouncedCallback } from 'use-debounce';

import useCheckOrderBookAccess from '../../../../../common/util/check-access/useCheckOrderBookAccess';
import routeFactory from '../../../../../common/util/routeFactory';
import { useRefetchBackoff } from '../../../../../common/util/use-refetch-backoff';
import { ReactRouterDomainNavTabProps } from '../../../../../design-system/react-router/ReactRouterDomainNavTab';
import { OfferingStatus, OfferingType, OrderBookState } from '../../../../../graphql';
import { orderBookStateLabels } from '../../../../../types/domain/order-book/constants';
import { useCheckPrivatePlacementTrait } from '../../../../offering-setup/basic-info/hooks/useCheckPrivatePlacementTrait';
import {
  useOfferingNavbar_OrderBookSubNavItem_OrderBookQuery,
  useOfferingNavbar_OrderBookSubNavItem_OrderBookStatusChangedSubscription,
} from '../graphql';
import { orderBookStateColors } from './orderBookStatus.model';

export const PRIVATE_PLACEMENT_DEMAND_STATUSES: readonly OfferingStatus[] = [
  OfferingStatus.Live,
  OfferingStatus.Priced,
];

type Props = {
  readonly offeringId: UUID | undefined;
  readonly offeringType: OfferingType | undefined;
  readonly offeringStatus: OfferingStatus | undefined;
};

export const usePrivatePlacementDemandRoute = ({
  offeringId,
  offeringType,
  offeringStatus,
}: Props): ReactRouterDomainNavTabProps | undefined => {
  const isSellSide = useCheckAccountType('SELL_SIDE');
  const hasPrivatePlacementTrait = useCheckPrivatePlacementTrait();
  const { canRead: canReadOrderBook } = useCheckOrderBookAccess({ offeringId });

  const canViewNavItem = !!(
    isSellSide &&
    hasPrivatePlacementTrait &&
    canReadOrderBook &&
    offeringType === OfferingType.PrivatePlacement &&
    offeringId &&
    offeringStatus &&
    PRIVATE_PLACEMENT_DEMAND_STATUSES.includes(offeringStatus)
  );

  const { loading, data, refetch } = useOfferingNavbar_OrderBookSubNavItem_OrderBookQuery({
    variables: { id: offeringId ?? '' },
    skip: !canViewNavItem,
    fetchPolicy: 'cache-and-network',
  });

  const refetchBackoff = useRefetchBackoff();

  const handleStatusChangedDebounced = useDebouncedCallback(
    () => {
      void refetch();
    },
    refetchBackoff,
    { maxWait: refetchBackoff }
  );

  useOfferingNavbar_OrderBookSubNavItem_OrderBookStatusChangedSubscription({
    variables: { offeringId: offeringId ?? '' },
    skip: !canViewNavItem,
    // https://www.apollographql.com/docs/react/data/subscriptions/#subscriptionhookoptions-interface-ignoreresults
    ignoreResults: true,
    onData: ({ data: subscriptionResult }) => {
      if (subscriptionResult?.data?.orderBookStatusChanged) {
        handleStatusChangedDebounced();
      }
    },
  });

  if (!canViewNavItem) {
    return undefined;
  }

  const state = data?.orderBook.state ?? OrderBookState.NeverOpened;
  const label = orderBookStateLabels[state];
  const color = orderBookStateColors[state];

  return {
    href: routeFactory.privatePlacementDemand.getUrlPath({ offeringId }),
    label: (
      <Tooltip title={`Demand Status: ${label}`} placement="top" arrow>
        <span>Private Placement Demand</span>
      </Tooltip>
    ),
    iconPosition: 'start',
    icon: loading ? undefined : <FiberManualRecordIcon color={color} />,
  };
};
